<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ConditionRows from './ConditionRows.vue';

// Flow-level settings panel, shown when the Trigger node is selected:
// trigger conditions (phase A/C) and the schedule (phase B). Mutates the
// builder's reactive form; Builder#save merges these over the loaded
// settings so unknown keys survive round-trips.
const props = defineProps({
  form: { type: Object, required: true },
});
const { t } = useI18n();
const store = useStore();

// Mutations go through the (stable) builder form object, same convention as
// NodeSettings with the node data object.
const settingsForm = computed(() => props.form);

const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const lifecycleEnabled = computed(
  () => store.getters['customerLifecycle/isReady'] || false
);
const lifecycleStages = computed(
  () => store.getters['customerLifecycle/getActiveStages'] || []
);
const dayKeys = [0, 1, 2, 3, 4, 5, 6];

const inboxAssigned = inboxId => settingsForm.value.inbox_ids.includes(inboxId);
const toggleAssignedInbox = inboxId => {
  const ids = settingsForm.value.inbox_ids;
  const index = ids.indexOf(inboxId);
  if (index === -1) ids.push(inboxId);
  else ids.splice(index, 1);
};

// The schedule object is created lazily on first edit (same convention as
// the QuestionStep timeout) so an untouched flow keeps settings unchanged.
const ensureSchedule = () => {
  if (!settingsForm.value.schedule) {
    settingsForm.value.schedule = {
      enabled: false,
      active_from: '',
      active_until: '',
      inbox_ids: [],
      weekly: [],
    };
  }
  return settingsForm.value.schedule;
};

const scheduleEnabled = computed({
  get: () => Boolean(props.form.schedule?.enabled),
  set: value => {
    ensureSchedule().enabled = value;
  },
});
const activeFrom = computed({
  get: () => props.form.schedule?.active_from || '',
  set: value => {
    ensureSchedule().active_from = value;
  },
});
const activeUntil = computed({
  get: () => props.form.schedule?.active_until || '',
  set: value => {
    ensureSchedule().active_until = value;
  },
});

const inboxAllowed = inboxId =>
  (props.form.schedule?.inbox_ids || []).includes(inboxId);
const toggleInbox = inboxId => {
  const ids = ensureSchedule().inbox_ids;
  const index = ids.indexOf(inboxId);
  if (index === -1) ids.push(inboxId);
  else ids.splice(index, 1);
};

const weekly = computed(() => props.form.schedule?.weekly || []);
const addWindow = () =>
  ensureSchedule().weekly.push({
    days: [1, 2, 3, 4, 5],
    from: '09:00',
    to: '17:00',
  });
const removeWindow = index => ensureSchedule().weekly.splice(index, 1);
const dayActive = (window, day) => (window.days || []).includes(day);
const toggleDay = (window, day) => {
  const days = window.days || (window.days = []);
  const index = days.indexOf(day);
  if (index === -1) days.push(day);
  else days.splice(index, 1);
};
</script>

<template>
  <div class="flex flex-col gap-3 text-sm">
    <p class="font-medium text-n-slate-12">
      {{ t('TELETEL_FLOWS.BUILDER.FLOW_SETTINGS.TITLE') }}
    </p>

    <div class="flex flex-col gap-1">
      <label>{{
        t('TELETEL_FLOWS.BUILDER.FLOW_SETTINGS.TRIGGER_CONDITIONS')
      }}</label>
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FLOW_SETTINGS.TRIGGER_CONDITIONS_HINT') }}
      </p>
      <ConditionRows
        :conditions="form.trigger_conditions"
        :lifecycle-enabled="lifecycleEnabled"
        :lifecycle-stages="lifecycleStages"
      />
    </div>

    <div class="flex flex-col gap-1 pt-2 border-t border-n-weak">
      <label class="font-medium">
        {{ t('TELETEL_FLOWS.BUILDER.FLOW_SETTINGS.INBOXES') }}
      </label>
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FLOW_SETTINGS.INBOXES_HINT') }}
      </p>
      <label
        v-for="inbox in inboxes"
        :key="inbox.id"
        class="flex items-center gap-1"
      >
        <input
          type="checkbox"
          class="mb-0"
          :checked="inboxAssigned(inbox.id)"
          @change="toggleAssignedInbox(inbox.id)"
        />
        {{ inbox.name }}
      </label>
    </div>

    <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
      <label class="flex items-center gap-1 font-medium">
        <input v-model="scheduleEnabled" type="checkbox" class="mb-0" />
        {{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.ENABLED') }}
      </label>

      <template v-if="scheduleEnabled">
        <label>{{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.ACTIVE_FROM') }}</label>
        <input v-model="activeFrom" type="datetime-local" class="mb-0" />
        <label>{{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.ACTIVE_UNTIL') }}</label>
        <input v-model="activeUntil" type="datetime-local" class="mb-0" />

        <label>{{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.INBOXES') }}</label>
        <p class="text-xs text-n-slate-10">
          {{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.INBOXES_HINT') }}
        </p>
        <label
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center gap-1"
        >
          <input
            type="checkbox"
            class="mb-0"
            :checked="inboxAllowed(inbox.id)"
            @change="toggleInbox(inbox.id)"
          />
          {{ inbox.name }}
        </label>

        <label>{{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.WEEKLY') }}</label>
        <div
          v-for="(window, index) in weekly"
          :key="index"
          class="flex flex-col gap-1 p-2 border rounded-lg border-n-weak"
        >
          <div class="flex flex-wrap gap-1">
            <button
              v-for="day in dayKeys"
              :key="day"
              type="button"
              class="px-2 py-0.5 text-xs border rounded-full"
              :class="
                dayActive(window, day)
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleDay(window, day)"
            >
              {{ t(`TELETEL_FLOWS.BUILDER.SCHEDULE.DAY_${day}`) }}
            </button>
          </div>
          <div class="flex items-center gap-2">
            <input v-model="window.from" type="time" class="w-28 mb-0" />
            <span class="text-n-slate-10">—</span>
            <input v-model="window.to" type="time" class="w-28 mb-0" />
            <button
              type="button"
              class="text-xs text-n-ruby-11"
              @click="removeWindow(index)"
            >
              {{ t('TELETEL_FLOWS.BUILDER.SCHEDULE.REMOVE_WINDOW') }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="text-n-brand text-start"
          @click="addWindow"
        >
          {{ `+ ${t('TELETEL_FLOWS.BUILDER.SCHEDULE.ADD_WINDOW')}` }}
        </button>
      </template>
    </div>
  </div>
</template>
